/**
 * Wiki architecture — single source of truth for the country-encyclopedia
 * product.  Drives routes, sitemap, prerender, hreflang, content lookup.
 *
 * Adding a new language: append to LANGUAGES; everything downstream is
 * data-driven (sitemap generator, prerender script, route definitions,
 * SEO meta helpers).
 *
 * Adding a new topic: append to TOPICS with a localized label map and a
 * content-importer registration in `content/index.ts`.
 *
 * Adding a new country: extend countries.json (already the upstream source)
 * and add a slug entry to COUNTRY_SLUGS.
 */

/* ────────────────────────────────────────────────────────────────── */
/*  Languages                                                          */
/* ────────────────────────────────────────────────────────────────── */

export interface LanguageDef {
  /** BCP-47 / ISO-639-1 code used in <html lang>, hreflang, URL prefix. */
  code: string;
  /** Native label for the language switcher. */
  label: string;
  /** Default = canonical / x-default. Only one entry may be default=true. */
  default: boolean;
  /** Locale string for og:locale (e.g. 'en_US', 'ru_RU'). */
  ogLocale: string;
}

export const LANGUAGES: readonly LanguageDef[] = [
  { code: 'en', label: 'English',  default: true,  ogLocale: 'en_US' },
  { code: 'ru', label: 'Русский', default: false, ogLocale: 'ru_RU' },
] as const;

export type LangCode = (typeof LANGUAGES)[number]['code'];

export const DEFAULT_LANG: LangCode =
  (LANGUAGES.find((l) => l.default)?.code ?? 'en') as LangCode;

export const LANG_CODES: readonly LangCode[] = LANGUAGES.map((l) => l.code as LangCode);

export function isLangCode(v: unknown): v is LangCode {
  return typeof v === 'string' && LANG_CODES.includes(v as LangCode);
}

export function getLangDef(code: LangCode): LanguageDef {
  return LANGUAGES.find((l) => l.code === code) ?? LANGUAGES[0];
}

/* ────────────────────────────────────────────────────────────────── */
/*  Country slug map                                                   */
/* ────────────────────────────────────────────────────────────────── */
/*  URL slugs use full English country names (lower-kebab) — they read */
/*  naturally for users + win on long-tail SEO. ISO codes stay as the  */
/*  internal key used by JSON datasets.                                */

export type CountryCode =
  // Core 30 — original roster
  | 'AE' | 'PT' | 'ES' | 'CY' | 'TH' | 'MY' | 'ID' | 'GR' | 'MU' | 'IT'
  | 'CR' | 'MT' | 'PA' | 'EE' | 'ME' | 'RS' | 'GE' | 'MX' | 'SG' | 'UY'
  | 'AL' | 'HU' | 'BR' | 'PY' | 'VN' | 'AR' | 'TR' | 'HR' | 'ZA' | 'CO'
  // Europe expansion — fully scored
  | 'AT'
  // Career-track expansion (CV.2 M4+) — stubs; full content deferred
  | 'CA' | 'CL' | 'CZ' | 'DE' | 'FR' | 'GB' | 'IE' | 'NL' | 'PL';

export const COUNTRY_SLUGS: Record<CountryCode, string> = {
  // Core 30
  AE: 'uae',
  PT: 'portugal',
  ES: 'spain',
  CY: 'cyprus',
  TH: 'thailand',
  MY: 'malaysia',
  ID: 'indonesia',
  GR: 'greece',
  MU: 'mauritius',
  IT: 'italy',
  CR: 'costa-rica',
  MT: 'malta',
  PA: 'panama',
  EE: 'estonia',
  ME: 'montenegro',
  RS: 'serbia',
  GE: 'georgia',
  MX: 'mexico',
  SG: 'singapore',
  UY: 'uruguay',
  AL: 'albania',
  HU: 'hungary',
  BR: 'brazil',
  PY: 'paraguay',
  VN: 'vietnam',
  AR: 'argentina',
  TR: 'turkey',
  HR: 'croatia',
  ZA: 'south-africa',
  CO: 'colombia',
  // Europe expansion — fully scored
  AT: 'austria',
  // Career-track expansion
  CA: 'canada',
  CL: 'chile',
  CZ: 'czechia',
  DE: 'germany',
  FR: 'france',
  GB: 'united-kingdom',
  IE: 'ireland',
  NL: 'netherlands',
  PL: 'poland',
};

export const ALL_COUNTRY_CODES: readonly CountryCode[] =
  Object.keys(COUNTRY_SLUGS) as CountryCode[];

const SLUG_TO_CODE: Record<string, CountryCode> = Object.entries(COUNTRY_SLUGS).reduce(
  (acc, [code, slug]) => ({ ...acc, [slug]: code as CountryCode }),
  {} as Record<string, CountryCode>,
);

export function slugToCountryCode(slug: string | undefined | null): CountryCode | null {
  if (!slug) return null;
  return SLUG_TO_CODE[slug.toLowerCase()] ?? null;
}

export function countryCodeToSlug(code: string | undefined | null): string | null {
  if (!code) return null;
  return COUNTRY_SLUGS[code.toUpperCase() as CountryCode] ?? null;
}

/* ────────────────────────────────────────────────────────────────── */
/*  Topics                                                             */
/* ────────────────────────────────────────────────────────────────── */
/*  Each topic = a SEO-targeted article page within a country.         */
/*  Order in this list = display order in the country-hub topic grid.  */

export interface TopicDef {
  /** URL slug (kebab-case, English — same in every language). */
  slug: string;
  /** i18n key under `wiki.topic.<slug>.label` / `.description`. */
  i18nKey: string;
  /** Emoji used as a soft visual marker — replace with custom icons later. */
  icon: string;
  /** Underlying quiz dimension for visual styling — maps to DIM_COLORS. */
  dimension: 'visa' | 'cost' | 'tax' | 'climate' | 'family' | 'safety' | 'language' | 'meta';
}

export const TOPICS: readonly TopicDef[] = [
  { slug: 'visa',            i18nKey: 'visa',          icon: '🛂', dimension: 'visa'     },
  { slug: 'taxes',           i18nKey: 'taxes',         icon: '🧮', dimension: 'tax'      },
  { slug: 'cost-of-living',  i18nKey: 'cost-of-living', icon: '💶', dimension: 'cost'    },
  { slug: 'property',        i18nKey: 'property',      icon: '🏠', dimension: 'cost'    },
  { slug: 'climate',         i18nKey: 'climate',       icon: '🌤', dimension: 'climate'  },
  { slug: 'healthcare',      i18nKey: 'healthcare',    icon: '🏥', dimension: 'family'   },
  { slug: 'education',       i18nKey: 'education',     icon: '🎓', dimension: 'family'   },
  { slug: 'banking',         i18nKey: 'banking',       icon: '🏦', dimension: 'meta'     },
  { slug: 'transport',       i18nKey: 'transport',     icon: '🚗', dimension: 'cost'    },
  { slug: 'safety-community', i18nKey: 'safety-community', icon: '🛡', dimension: 'safety'   },
  { slug: 'language-culture', i18nKey: 'language-culture', icon: '💬', dimension: 'language' },
  { slug: 'infrastructure',  i18nKey: 'infrastructure', icon: '📡', dimension: 'meta'    },
] as const;

export type TopicSlug = (typeof TOPICS)[number]['slug'];

export const TOPIC_SLUGS: readonly TopicSlug[] = TOPICS.map((t) => t.slug);

export function isTopicSlug(v: unknown): v is TopicSlug {
  return typeof v === 'string' && TOPIC_SLUGS.includes(v as TopicSlug);
}

export function getTopicDef(slug: TopicSlug): TopicDef {
  // Safe: caller has already passed isTopicSlug — fall back to first entry
  // to satisfy types without throwing.
  return TOPICS.find((t) => t.slug === slug) ?? TOPICS[0];
}

/* ────────────────────────────────────────────────────────────────── */
/*  URL helpers                                                        */
/* ────────────────────────────────────────────────────────────────── */
/*  Public shape (no lang prefix for default language):                */
/*    /countries                       — atlas root (EN)               */
/*    /ru/countries                    — atlas root (RU)               */
/*    /countries/italy                 — Italy hub (EN)                */
/*    /ru/countries/italy              — Italy hub (RU)                */
/*    /countries/italy/visa            — Italy visa article (EN)       */
/*    /ru/countries/italy/visa         — Italy visa article (RU)       */

interface BuildUrlOpts {
  lang?: LangCode;
  /** Country code (ISO-3166-α2) — converted to slug internally. */
  cc?: CountryCode;
  topic?: TopicSlug;
  /** Absolute base for canonical / sitemap (e.g. https://lodestar.place). */
  origin?: string;
}

export function buildWikiUrl(opts: BuildUrlOpts = {}): string {
  const lang = opts.lang ?? DEFAULT_LANG;
  const prefix = lang === DEFAULT_LANG ? '' : `/${lang}`;
  const parts: string[] = ['countries'];
  if (opts.cc) {
    const slug = countryCodeToSlug(opts.cc);
    if (slug) parts.push(slug);
    if (opts.topic) parts.push(opts.topic);
  }
  const path = `${prefix}/${parts.join('/')}`;
  return opts.origin ? `${opts.origin}${path}` : path;
}

export interface ParsedWikiPath {
  lang: LangCode;
  cc: CountryCode | null;
  topic: TopicSlug | null;
}

/**
 * Parse a wiki pathname into structured pieces.  Returns null for `cc` or
 * `topic` if the slug is unknown — the page component is responsible for
 * rendering 404 in that case.
 */
export function parseWikiPath(pathname: string): ParsedWikiPath | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  let cursor = 0;
  let lang: LangCode = DEFAULT_LANG;

  // Optional leading lang prefix (only for non-default languages).
  if (isLangCode(segments[cursor]) && segments[cursor] !== DEFAULT_LANG) {
    lang = segments[cursor] as LangCode;
    cursor += 1;
  }

  if (segments[cursor] !== 'countries') return null;
  cursor += 1;

  const cc = slugToCountryCode(segments[cursor]);
  cursor += cc ? 1 : 0;

  const topicSeg = segments[cursor];
  const topic = isTopicSlug(topicSeg) ? topicSeg : null;

  return { lang, cc, topic };
}

/* ────────────────────────────────────────────────────────────────── */
/*  Hreflang builder                                                   */
/* ────────────────────────────────────────────────────────────────── */
/*  Returns one alternate per language + an x-default pointing to the  */
/*  default-language URL.                                              */

export interface HreflangEntry {
  hreflang: string; // 'en' | 'ru' | … | 'x-default'
  href: string;     // absolute URL
}

export function buildHreflangAlternates(
  base: Omit<BuildUrlOpts, 'lang'> & { origin: string },
): HreflangEntry[] {
  const entries: HreflangEntry[] = LANGUAGES.map((l) => ({
    hreflang: l.code,
    href: buildWikiUrl({ ...base, lang: l.code as LangCode }),
  }));
  entries.push({
    hreflang: 'x-default',
    href: buildWikiUrl({ ...base, lang: DEFAULT_LANG }),
  });
  return entries;
}
